import { GlobalHelper } from '../utils/GlobalHelper';
import { ArticleActions } from './ArticleActions';
import { PropertyEditorActions } from './PropertyEditorActions';
import { AppConfigurationStore, EaiwsGatekeeperSetup, EaiwsManualSetup } from '../stores/AppConfigurationStore';
import { EAIWS_FALLBACK_LANGUAGES } from '../constants/Api';
import { Languages, LanguageSetting } from '../constants/Languages';
import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { CatalogActions } from './CatalogActions';
import { ProjectSettingsActions } from './ProjectSettingsActions';
import { UrlHelper } from '../utils/UrlHelper';
import { PconLoginStore } from '../stores/PconLoginStore';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';

import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;
import { ignoreAjaxErrorHandlerMessageCallback, AjaxErrorHandler } from '../submodules/utils/AjaxErrorHandler';
import { fallbackAction } from '../submodules/utils/Promise';

interface GatekeeperResponse {
    server: string;
    sessionId: string;
}

export abstract class SessionActions {
    public static async startEaiwsSession(dataLanguage: string, localeLanguage: string): Promise<void> {
        // close current session first
        if (GlobalHelper.eaiwsSession.session != null) {
            await GlobalHelper.eaiwsSession.close();
        }

        // open new session
        await SessionActions.openEaiwsSession(dataLanguage, PconLoginStore.getPackageGroups());

        await SessionActions.setLanguage(dataLanguage, false);
        await SessionActions.setLocale(localeLanguage);
        await SessionActions.setImageFormat();

        // Update catalog:
        CatalogActions.leaveCatalogItem();

        // Set currency and exchange rates
        await GlobalHelper.eaiwsSession.basket.setCurrency(ProjectSettingsStore.getState().currency);
        const exchangeRates: Array<eaiws.basket.ExchangeRate> = await GlobalHelper.eaiwsSession.basket.getExchangeRates();
        const map: Map<string, number | undefined> = new Map();
        for (const rate of exchangeRates) {
            map.set(rate.currency, rate.rate);
        }

        AppDispatcher.dispatch({
            actionType: ActionConstants.UPDATE_EXCHANGE_RATES,
            value: map
        });
    }

    public static async setImageFormat(): Promise<void> {
        const value: string = '.' + AppConfigurationStore.getState().propertyImageFormats.join(',.');
        await GlobalHelper.eaiwsSession.session.setSessionProperty(
            'egr.eai.ws.basket.material_icon_format',
            value
        );
    }

    public static async loadObkFile(obkFile: Blob, overrideCurrency: boolean = true): Promise<void> {
        const url: string | null = await GlobalHelper.eaiwsSession.uploadFile('Project', obkFile);
        if (utils.string.isNullOrEmpty(url)) {
            return;
        }

        await GlobalHelper.eaiwsSession.session.loadSession(url);
        await GlobalHelper.eaiwsSession.deleteFile(url);

        await GlobalHelper.articleManager.importFromSession({
            shouldUpdateCallback: (pItemProps: eaiws.basket.ItemProperties): boolean => {
                return pItemProps.article != null && pItemProps.article.ofmlUpdateState === 'Migratable';
            },
            importArticlesWithoutPosition: true
        });

        if (overrideCurrency) {
            await GlobalHelper.eaiwsSession.basket.setCurrency(ProjectSettingsStore.getState().currency);
        } else {
            const currency: string = await GlobalHelper.eaiwsSession.basket.getCurrency();
            ProjectSettingsActions.setCurrency(currency);
        }

        ArticleActions.invalidateArticleList(true);
        GlobalHelper.coreApp.viewer.requestRenderFrame();
    }

    public static async loadPecFile(pecFile: Blob | ArrayBuffer): Promise<Array<core.mdl.SceneElement>> {
        const pecImport: cf.io.PecImport = new cf.io.PecImport(GlobalHelper.articleManager);
        const elements: Array<core.mdl.SceneElement> = await pecImport.import(pecFile);
        utils.planning.placeElementsInViewCenter(elements, GlobalHelper.activeView, GlobalHelper.coreApp.model);

        const insertCommand: core.cmd.InsertElements = new core.cmd.InsertElements(GlobalHelper.coreApp, elements);
        GlobalHelper.coreApp.commands.executeCommand(insertCommand);
        GlobalHelper.coreApp.viewer.requestRenderFrame();

        return elements;
    }

    public static setCurrency(currency: string): Promise<void> {
        if (!GlobalHelper.eaiwsSession.isValid) {
            return Promise.resolve();
        }
        return GlobalHelper.eaiwsSession.basket.setCurrency(currency);
    }

    public static async setLocale(language: string): Promise<void> {
        if (GlobalHelper.eaiwsSession.isValid) {
            const selectedLanguage: LanguageSetting = Languages.filter((languageInfo) => {
                return languageInfo.short === language;
            })[0];
            if (selectedLanguage != null) {
                await GlobalHelper.eaiwsSession.session.setLocale(selectedLanguage.locale);
            }
        }
    }

    public static async setLanguage(language: string, resetCache: boolean = true): Promise<void> {
        if (GlobalHelper.eaiwsSession.isValid) {
            const languages: Array<string> = [language].concat(
                EAIWS_FALLBACK_LANGUAGES
            ).filter(
                AppConfigurationStore.dataLanguageFilter
            );

            // set catalog language
            await GlobalHelper.eaiwsSession.catalog.setLanguages(languages);

            // set data language
            await GlobalHelper.eaiwsSession.basket.setLanguages(languages);

            if (resetCache) {
                GlobalHelper.articleManager.resetCachedArticleInfos(true);
                ArticleActions.invalidateArticleList();
                PropertyEditorActions.invalidateProperties();
            }
        }
    }

    protected static async openEaiwsSession(dataLanguage: string, packageGroups: Array<string> | null): Promise<void> {
        const packageString: string = packageGroups != null ? packageGroups.join(',') : '';
        const usePackageGroups: boolean = utils.string.isNotNullOrEmpty(packageString);
        const setup: EaiwsGatekeeperSetup | EaiwsManualSetup = AppConfigurationStore.getEaiwsSetup(usePackageGroups);

        if (setup.type === 'manual') {
            await GlobalHelper.eaiwsSession.open(
                UrlHelper.stripTrailingSlash(setup.server),
                // @ts-ignore
                {
                    startup: setup.startup,
                    packageGroups: packageString,
                    keepAliveInterval: AppConfigurationStore.getState().eaiws_keepalive,
                }
            );
        } else {
            const data: string = !usePackageGroups ? '{}' : JSON.stringify({
                packageGroups: packageGroups
            });
            const sessionInfo: GatekeeperResponse = await SessionActions.requestSession(setup.id, dataLanguage, data);

            GlobalHelper.eaiwsSession.connect(
                UrlHelper.stripTrailingSlash(sessionInfo.server),
                sessionInfo.sessionId,
                AppConfigurationStore.getState().eaiws_keepalive
            );
        }
    }

    protected static async requestSession(gatekeeperId: string, dataLanguage: string, data: string): Promise<GatekeeperResponse> {
        let errorHandlerCalled: boolean = false;
        const errorHandler: AjaxErrorHandler = (error: Error | string, xhr: XMLHttpRequest, response: unknown): Error => {
            if (errorHandlerCalled) {
                return error instanceof Error ? error : new Error(error);
            }

            errorHandlerCalled = true;
            return ignoreAjaxErrorHandlerMessageCallback(error, xhr, response);
        };

        return fallbackAction<GatekeeperResponse>(
            (): Promise<GatekeeperResponse> => {
                return utils.async.ajax<GatekeeperResponse>(
                    'POST',
                    'https://eaiws-server.pcon-solutions.com/v2/session/' + gatekeeperId + '?' + UrlHelper.buildQueryString({
                        dataLanguage
                    }),
                    data,
                    {
                        dataType: 'text',
                        headers: {
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    },
                    errorHandler
                );
            },
            (): Promise<GatekeeperResponse> => {
                return utils.async.ajax<GatekeeperResponse>(
                    'POST',
                    'https://eaiws-server-fallback.pcon-solutions.com/v2/session/' + gatekeeperId + '?' + UrlHelper.buildQueryString({
                        dataLanguage
                    }),
                    data,
                    {
                        dataType: 'text',
                        headers: {
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    },
                    errorHandler
                );
            },
            10000
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/SessionActions.ts